<template>
  <div class="px-5 sm:px-6 lg:px-10 py-6" v-cloak>
    <div class="mb-6">
      <h1 class="text-3xl font-bold text-gray-900 leading-7">
        Send Via WhatsApp
      </h1>
    </div>

    <!-- Daily messaging limit banner -->
    <div
      v-if="messagingLimit"
      class="mb-6 flex items-center justify-between gap-4 rounded-xl border p-3 text-sm"
      :class="
        messagingLimit.limit_reached
          ? 'border-red-200 bg-red-50 text-red-700'
          : 'border-gray-200 bg-gray-50 text-gray-700'
      "
    >
      <div class="font-medium">
        <template v-if="messagingLimit.limit === null">
          Unlimited daily messaging tier
        </template>
        <template v-else>
          {{ messagingLimit.used }} / {{ messagingLimit.limit }} unique users
          messaged in the last 24h
          <span v-if="messagingLimit.limit_reached" class="font-bold">
            — daily limit reached</span
          >
        </template>
      </div>
      <div v-if="messagingLimit.limit !== null" class="text-xs">
        {{ messagingLimit.remaining }} remaining today
      </div>
    </div>

    <div class="flex items-center justify-between gap-4 mb-12">
      <div class="w-full max-w-xs">
        <input
          v-model="topSearch"
          type="text"
          placeholder="Search"
          class="w-full h-9 rounded-lg border border-gray-300 pl-9 pr-3 text-sm focus:outline-none focus:ring-0"
        />
      </div>

      <button
        type="button"
        class="inline-flex items-center gap-2 h-9 px-4 rounded-full font-bold text-white text-sm shadow-sm hover:opacity-95"
        style="background-color: #5b9ac5"
        @click="openAdd"
      >
        <span class="text-base leading-none">+</span>
        <span>New Campaign</span>
      </button>
    </div>

    <DynamicTable
      wrapperClass="max-w-full"
      :rows="rows"
      :columns="columns"
      :rowsPerPage="10"
      :rowsPerPageOptions="[10, 20, 50]"
      :showHeader="false"
      :externalSearch="topSearch"
      :loading="loadingCampaigns"
    >
      <template #status="{ row }">
        <span
          class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold"
          :class="statusClass(row.status)"
        >
          {{ row.status }}
        </span>
      </template>

      <template #progress="{ row }">
        <span class="text-xs text-gray-700">
          {{
            row.sent_count +
            row.delivered_count +
            row.read_count +
            row.failed_count
          }}
          / {{ row.total_recipients }}
        </span>
      </template>

      <template #action="{ row }">
        <div class="flex items-center justify-center gap-5">
          <button
            class="inline-flex items-center gap-1 text-gray-700 hover:text-gray-900"
            @click="openView(row)"
          >
            <i class="fas fa-eye"></i>
            <span>View</span>
          </button>
        </div>
      </template>
    </DynamicTable>

    <!-- Add/View Modal -->
    <Teleport to="body">
      <div
        v-if="modalOpen"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4 overflow-y-auto backdrop-blur-sm"
      >
        <div
          class="bg-white w-full max-w-xl rounded-2xl shadow-2xl border border-gray-100 overflow-hidden relative flex flex-col max-h-[82vh] mt-12"
        >
          <!-- Modal Header -->
          <div
            class="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between bg-gray-50/50 shrink-0"
          >
            <h3 class="text-base font-bold text-gray-800">
              {{
                modalMode === "add"
                  ? "New WhatsApp Campaign"
                  : "Campaign Details"
              }}
            </h3>
            <button
              type="button"
              class="w-7 h-7 rounded-full hover:bg-gray-200/60 text-gray-500 flex items-center justify-center transition-colors"
              @click="closeModal"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          <!-- Modal Body -->
          <div class="p-5 overflow-y-auto flex-1 space-y-4">
            <!-- VIEW MODE -->
            <div v-if="modalMode === 'view'" class="space-y-4">
              <div
                v-if="loadingDetail"
                class="text-center text-sm text-gray-500 py-6"
              >
                Loading...
              </div>

              <template v-else>
                <img
                  v-if="viewCampaign?.header_image_url"
                  :src="viewCampaign.header_image_url"
                  class="w-full h-40 object-cover rounded-xl border border-gray-100"
                />

                <div
                  class="grid grid-cols-2 gap-3 text-sm bg-gray-50 p-4 rounded-xl"
                >
                  <div>
                    <span class="font-semibold text-gray-700">Template:</span>
                    <span class="text-gray-900 ml-1">{{
                      viewCampaign?.template_name
                    }}</span>
                  </div>
                  <div>
                    <span class="font-semibold text-gray-700">Status:</span>
                    <span class="text-gray-900 ml-1">{{
                      viewCampaign?.status
                    }}</span>
                  </div>
                  <div class="col-span-2">
                    <span class="font-semibold text-gray-700">Audience:</span>
                    <span class="text-gray-900 ml-1">{{
                      viewCampaign?.audience_label || viewCampaign?.category
                    }}</span>
                  </div>
                  <div>
                    <span class="font-semibold text-gray-700">Total:</span>
                    <span class="text-gray-900 ml-1">{{
                      viewCampaign?.total_recipients
                    }}</span>
                  </div>
                  <div>
                    <span class="font-semibold text-gray-700">Sent:</span>
                    <span class="text-gray-900 ml-1">{{
                      viewCampaign?.sent_count
                    }}</span>
                  </div>
                  <div>
                    <span class="font-semibold text-gray-700">Delivered:</span>
                    <span class="text-gray-900 ml-1">{{
                      viewCampaign?.delivered_count
                    }}</span>
                  </div>
                  <div>
                    <span class="font-semibold text-gray-700">Read:</span>
                    <span class="text-gray-900 ml-1">{{
                      viewCampaign?.read_count
                    }}</span>
                  </div>
                  <div>
                    <span class="font-semibold text-gray-700">Failed:</span>
                    <span class="text-gray-900 ml-1">{{
                      viewCampaign?.failed_count
                    }}</span>
                  </div>
                </div>

                <div
                  v-if="viewScheduledCount > 0"
                  class="text-xs bg-amber-50 border border-amber-100 text-amber-700 rounded-lg px-3 py-2"
                >
                  {{ viewScheduledCount }} recipient(s) scheduled for later once
                  the daily messaging limit resets.
                </div>

                <div v-if="viewRecipients.length" class="mt-2">
                  <p class="text-sm font-semibold text-gray-700 mb-2">
                    Recipients
                  </p>
                  <div
                    class="max-h-48 overflow-y-auto border border-gray-200 rounded-xl divide-y bg-white"
                  >
                    <div
                      v-for="r in viewRecipients"
                      :key="r.id"
                      class="flex items-center justify-between px-3 py-2 text-xs"
                    >
                      <span class="font-medium text-gray-800">{{
                        r.customer_name || r.phone
                      }}</span>
                      <div class="flex items-center gap-2">
                        <span
                          v-if="r.status === 'scheduled' && r.scheduled_at"
                          class="text-[10px] text-gray-400"
                        >
                          {{ formatScheduledAt(r.scheduled_at) }}
                        </span>
                        <span
                          class="px-2 py-0.5 rounded-full font-semibold"
                          :class="statusClass(r.status)"
                        >
                          {{ r.status }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <button
                type="button"
                class="w-full h-10 rounded-xl font-bold text-white shadow-sm transition-opacity hover:opacity-95"
                style="background-color: #5b9ac5"
                @click="closeModal"
              >
                Close
              </button>
            </div>

            <!-- ADD MODE -->
            <form v-else class="space-y-4" @submit.prevent="submitCampaign">
              <!-- Template picker -->
              <div class="space-y-1">
                <label
                  class="text-xs font-bold text-gray-700 uppercase tracking-wider"
                  >Template</label
                >
                <select
                  v-model="selectedTemplateName"
                  class="w-full h-10 rounded-xl border border-gray-200 px-3 text-sm focus:outline-none focus:border-[#5b9ac5] focus:ring-1 focus:ring-[#5b9ac5] bg-white transition-all shadow-sm"
                  @change="onTemplateChange"
                >
                  <option value="" disabled>Select an approved template</option>
                  <option
                    v-for="t in approvedTemplates"
                    :key="t.name"
                    :value="t.name"
                  >
                    {{ t.name }}
                  </option>
                </select>
                <p
                  v-if="!approvedTemplates.length"
                  class="text-xs text-amber-600 mt-1"
                >
                  No approved templates yet - create one in Template Management
                  first.
                </p>
              </div>

              <!-- Header image (only if the selected template requires one) -->
              <div
                v-if="selectedTemplate?.header_type === 'IMAGE'"
                class="space-y-1"
              >
                <label
                  class="text-xs font-bold text-gray-700 uppercase tracking-wider"
                  >Header Image</label
                >

                <div
                  v-if="headerImagePreview"
                  class="relative rounded-xl border border-gray-200 overflow-hidden shadow-sm"
                >
                  <img
                    :src="headerImagePreview"
                    class="w-full h-32 object-cover"
                  />
                  <button
                    type="button"
                    class="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/90 hover:bg-white shadow flex items-center justify-center transition-colors text-xs"
                    @click="removeHeaderImage"
                  >
                    ✕
                  </button>
                  <span
                    v-if="uploadingHeaderImage"
                    class="absolute inset-0 bg-black/40 flex items-center justify-center text-white text-xs font-semibold"
                  >
                    Uploading...
                  </span>
                </div>

                <div
                  v-else
                  class="rounded-xl border border-dashed border-gray-300 p-3 text-center cursor-pointer hover:bg-gray-50/80 transition-colors"
                  @click="$refs.headerImageInput.click()"
                >
                  <div class="text-xs font-medium text-gray-800">
                    Click to upload header image
                  </div>
                  <div class="text-[10px] text-gray-400 mt-0.5">
                    PNG or JPEG supported
                  </div>
                </div>

                <input
                  ref="headerImageInput"
                  type="file"
                  accept="image/png,image/jpeg"
                  class="hidden"
                  @change="onHeaderImageChange"
                />
              </div>

              <!-- Body preview + variable inputs -->
              <div
                v-if="selectedTemplate"
                class="p-3.5 bg-gray-50/70 border border-gray-100 rounded-xl space-y-2.5"
              >
                <div>
                  <p
                    class="text-[11px] font-bold text-gray-700 uppercase tracking-wider mb-1"
                  >
                    Message Preview
                  </p>
                  <p
                    class="text-xs text-gray-800 bg-white border border-gray-200/80 rounded-lg p-2.5 whitespace-pre-wrap shadow-sm leading-relaxed"
                  >
                    {{ previewText }}
                  </p>
                </div>

                <div v-if="manualVariables.length" class="space-y-1.5 pt-1">
                  <p
                    class="text-[11px] font-bold text-gray-700 uppercase tracking-wider"
                  >
                    Template Variables
                  </p>
                  <div v-for="v in manualVariables" :key="v" class="space-y-1">
                    <label class="text-[11px] font-medium text-gray-600">{{
                      v
                    }}</label>
                    <input
                      v-model="variableInputs[v]"
                      type="text"
                      class="w-full h-8 rounded-lg border border-gray-200 px-2.5 text-xs focus:outline-none focus:border-[#5b9ac5] bg-white shadow-sm"
                      placeholder="Enter value..."
                    />
                  </div>
                </div>
              </div>

              <!-- Multi-Type Customer Audience Selection -->
              <div class="space-y-2">
                <label
                  class="text-xs font-bold text-gray-700 uppercase tracking-wider"
                  >Target Audience Groups</label
                >

                <!-- Checkbox group for multiple audience types -->
                <div class="grid grid-cols-3 gap-2">
                  <label
                    class="flex items-center gap-2 p-2.5 rounded-xl border border-gray-200 bg-white cursor-pointer hover:border-[#5b9ac5] transition-all select-none shadow-sm"
                    :class="{
                      'border-[#5b9ac5] bg-[#5b9ac5]/5 ring-1 ring-[#5b9ac5]':
                        selectedAudienceTypes.includes('our'),
                    }"
                  >
                    <input
                      type="checkbox"
                      value="our"
                      v-model="selectedAudienceTypes"
                      class="w-4 h-4 rounded text-[#5b9ac5] focus:ring-[#5b9ac5]"
                    />
                    <div class="text-[11px]">
                      <span class="font-bold text-gray-800 block"
                        >Our Customers</span
                      >
                      <span class="text-gray-500"
                        >({{ ourCustomers.length }})</span
                      >
                    </div>
                  </label>

                  <label
                    class="flex items-center gap-2 p-2.5 rounded-xl border border-gray-200 bg-white cursor-pointer hover:border-[#5b9ac5] transition-all select-none shadow-sm"
                    :class="{
                      'border-[#5b9ac5] bg-[#5b9ac5]/5 ring-1 ring-[#5b9ac5]':
                        selectedAudienceTypes.includes('new'),
                    }"
                  >
                    <input
                      type="checkbox"
                      value="new"
                      v-model="selectedAudienceTypes"
                      class="w-4 h-4 rounded text-[#5b9ac5] focus:ring-[#5b9ac5]"
                    />
                    <div class="text-[11px]">
                      <span class="font-bold text-gray-800 block"
                        >New Customers</span
                      >
                      <span class="text-gray-500"
                        >({{ newCustomers.length }})</span
                      >
                    </div>
                  </label>

                  <label
                    class="flex items-center gap-2 p-2.5 rounded-xl border border-gray-200 bg-white cursor-pointer hover:border-[#5b9ac5] transition-all select-none shadow-sm"
                    :class="{
                      'border-[#5b9ac5] bg-[#5b9ac5]/5 ring-1 ring-[#5b9ac5]':
                        selectedAudienceTypes.includes('company'),
                    }"
                  >
                    <input
                      type="checkbox"
                      value="company"
                      v-model="selectedAudienceTypes"
                      class="w-4 h-4 rounded text-[#5b9ac5] focus:ring-[#5b9ac5]"
                    />
                    <div class="text-[11px]">
                      <span class="font-bold text-gray-800 block">Company</span>
                      <span class="text-gray-500"
                        >({{ companyCustomers.length }})</span
                      >
                    </div>
                  </label>
                </div>

                <!-- Granular User Picker Accordion / Expansion Box -->
                <div
                  class="mt-2.5 border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm"
                >
                  <div
                    class="p-2.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between"
                  >
                    <span class="text-[11px] font-bold text-gray-700">
                      Refine Recipients ({{ eligibleTotal }} eligible selected)
                    </span>
                    <span class="text-[10px] text-gray-400"
                      >Excludes blacklisted numbers</span
                    >
                  </div>

                  <div class="p-2 border-b border-gray-100">
                    <input
                      v-model="usersSearch"
                      type="text"
                      placeholder="Search users by name..."
                      class="w-full h-8 rounded-lg border border-gray-200 px-2.5 text-xs focus:outline-none focus:border-[#5b9ac5] bg-gray-50/50"
                    />
                  </div>

                  <div class="max-h-36 overflow-y-auto p-2 space-y-1.5">
                    <div
                      class="flex items-center justify-between pb-1.5 border-b border-gray-100 px-1"
                    >
                      <label
                        class="flex items-center gap-2 select-none cursor-pointer text-[11px] font-semibold text-gray-700"
                      >
                        <input
                          type="checkbox"
                          class="w-3.5 h-3.5 rounded text-[#5b9ac5]"
                          :checked="allCurrentSelected"
                          @change="toggleAllCurrent"
                        />
                        <span>Select All Visible</span>
                      </label>
                      <span class="text-[10px] text-gray-400"
                        >{{ filteredCurrentUsers.length }} shown</span
                      >
                    </div>

                    <label
                      v-for="u in filteredCurrentUsers"
                      :key="u.id"
                      class="flex items-center gap-2 select-none cursor-pointer p-1 hover:bg-gray-50 rounded-md transition-colors"
                    >
                      <input
                        type="checkbox"
                        class="w-3.5 h-3.5 rounded text-[#5b9ac5] focus:ring-[#5b9ac5]"
                        :checked="selectedUserIds.includes(u.id)"
                        @change="toggleUser(u.id)"
                      />
                      <div
                        class="text-xs flex items-center justify-between flex-1"
                      >
                        <span class="font-medium text-gray-800">{{
                          u.name
                        }}</span>
                        <span class="text-gray-400 text-[10px]">{{
                          u.phone
                        }}</span>
                      </div>
                    </label>

                    <div
                      v-if="filteredCurrentUsers.length === 0"
                      class="text-xs text-center text-gray-500 py-4"
                    >
                      No users match your criteria.
                    </div>
                  </div>
                </div>

                <!-- Daily limit split preview -->
                <p
                  v-if="
                    messagingLimit &&
                    messagingLimit.limit !== null &&
                    eligibleTotal > 0
                  "
                  class="text-[11px] rounded-lg px-2.5 py-1.5 border"
                  :class="
                    messagingSplitPreview.deferred > 0
                      ? 'text-amber-700 bg-amber-50 border-amber-100'
                      : 'text-gray-500 bg-gray-50 border-gray-100'
                  "
                >
                  <template v-if="messagingSplitPreview.deferred > 0">
                    {{ messagingSplitPreview.sendNow }} will send today.
                    {{ messagingSplitPreview.deferred }} will be automatically
                    scheduled for once your daily limit resets (~24h from each
                    customer's first message today).
                  </template>
                  <template v-else>
                    All {{ messagingSplitPreview.sendNow }} recipients fit
                    within today's remaining limit ({{
                      messagingLimit.remaining
                    }}
                    left).
                  </template>
                </p>
              </div>

              <p
                v-if="formError"
                class="text-xs text-red-600 font-medium bg-red-50 p-2 rounded-lg border border-red-100"
              >
                {{ formError }}
              </p>

              <button
                type="submit"
                class="w-full h-10 rounded-xl font-bold text-white shadow-md transition-all hover:opacity-95 disabled:opacity-60 text-xs mt-1 shrink-0"
                style="background-color: #5b9ac5"
                :disabled="sending || uploadingHeaderImage"
              >
                {{
                  sending
                    ? "Sending Campaign..."
                    : `Send Campaign to ${eligibleTotal} Recipients`
                }}
              </button>
            </form>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import axios from "axios";
import DynamicTable from "@/components/DynamicTable.vue";
import Swal from "sweetalert2";
import { mapActions, mapState } from "vuex";

const marketingApi = axios.create({
  baseURL: "https://marketingvps.lyon-jo.com/api",
});
marketingApi.interceptors.request.use((config) => {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("access_token");
  if (token) config.headers.Authorization = "Bearer " + token;
  return config;
});

export default {
  name: "SendWhatsApp",
  components: { DynamicTable },

  data() {
    return {
      topSearch: "",
      campaigns: [],
      loadingCampaigns: false,

      columns: [
        { key: "id", label: "ID", align: "center", width: "60px" },
        {
          key: "template_name",
          label: "Template",
          align: "center",
          width: "200px",
        },
        { key: "audience_label", label: "To", align: "center", width: "160px" },
        { key: "progress", label: "Progress", align: "center", width: "120px" },
        { key: "status", label: "Status", align: "center", width: "120px" },
        {
          key: "created_at_display",
          label: "Date",
          align: "center",
          width: "140px",
        },
        { key: "action", label: "Action", align: "center", width: "100px" },
      ],

      templates: [],
      selectedTemplateName: "",
      variableInputs: {},

      headerImageFile: null,
      headerImagePreview: "",
      headerImageUrl: "",
      uploadingHeaderImage: false,

      modalOpen: false,
      modalMode: "add",
      viewCampaign: null,
      viewRecipients: [],
      loadingDetail: false,

      // Multi-type audience selectors
      selectedAudienceTypes: ["our", "new", "company"],
      usersSearch: "",
      selectedUserIds: [],

      formError: "",
      sending: false,
      customerNamePlaceholder: "{{customer_name}}",

      // Daily WhatsApp Cloud API unique-user messaging limit
      messagingLimit: null,
    };
  },

  computed: {
    ...mapState(["customers"]),

    newCustomers() {
      return this.customers?.new_customers || [];
    },
    ourCustomers() {
      return this.customers?.our_customers || [];
    },
    companyCustomers() {
      return this.customers?.company_customers || [];
    },

    newUsersForPicker() {
      return this.newCustomers.map((c) => ({
        id: c.id ?? "new-" + c.country_code + c.phone,
        name:
          ((c.first_name || "") + " " + (c.last_name || "")).trim() || c.phone,
        phone: (c.country_code || "") + (c.phone || ""),
      }));
    },
    ourUsersForPicker() {
      return this.ourCustomers.map((c) => ({
        id: c.id ?? "our-" + c.country_code + c.phone,
        name:
          ((c.first_name || "") + " " + (c.last_name || "")).trim() || c.phone,
        phone: (c.country_code || "") + (c.phone || ""),
      }));
    },
    companyUsersForPicker() {
      return this.companyCustomers.map((c) => ({
        id: c.id ?? "company-" + c.country_code + c.phone,
        name:
          ((c.first_name || "") + " " + (c.last_name || "")).trim() || c.phone,
        phone: (c.country_code || "") + (c.phone || ""),
      }));
    },

    currentUsersForPicker() {
      let list = [];
      if (this.selectedAudienceTypes.includes("new"))
        list = list.concat(this.newUsersForPicker);
      if (this.selectedAudienceTypes.includes("our"))
        list = list.concat(this.ourUsersForPicker);
      if (this.selectedAudienceTypes.includes("company"))
        list = list.concat(this.companyUsersForPicker);
      return list;
    },

    filteredCurrentUsers() {
      const q = (this.usersSearch || "").trim().toLowerCase();
      const list = this.currentUsersForPicker;
      if (!q) return list;
      return list.filter(
        (u) => u.name.toLowerCase().includes(q) || u.phone.includes(q),
      );
    },

    allCurrentSelected() {
      if (!this.currentUsersForPicker.length) return false;
      return this.currentUsersForPicker.every((u) =>
        this.selectedUserIds.includes(u.id),
      );
    },

    rows() {
      return this.campaigns.map((c) => ({
        ...c,
        created_at_display: c.created_at
          ? new Date(c.created_at).toLocaleString()
          : "",
      }));
    },

    approvedTemplates() {
      return this.templates.filter((t) => t.status === "APPROVED");
    },
    selectedTemplate() {
      return (
        this.approvedTemplates.find(
          (t) => t.name === this.selectedTemplateName,
        ) || null
      );
    },
    manualVariables() {
      return (this.selectedTemplate?.variables || []).filter(
        (v) => v !== "customer_name",
      );
    },
    usesCustomerName() {
      return (this.selectedTemplate?.variables || []).includes("customer_name");
    },
    previewText() {
      let body = this.selectedTemplate?.body_text || "";
      (this.selectedTemplate?.variables || []).forEach((v) => {
        const val =
          v === "customer_name"
            ? "{{customer_name}}"
            : this.variableInputs[v] || "[" + v + "]";
        body = body.replaceAll("{{" + v + "}}", val);
      });
      return body;
    },

    resolvedPhones() {
      const picked = this.selectedUserIds.length
        ? this.currentUsersForPicker.filter((u) =>
            this.selectedUserIds.includes(u.id),
          )
        : this.currentUsersForPicker;
      return picked.map((u) => u.phone);
    },

    eligibleTotal() {
      return new Set(this.resolvedPhones.filter(Boolean)).size;
    },

    audienceLabelSummary() {
      const labels = [];
      if (this.selectedAudienceTypes.includes("our"))
        labels.push("Our Customers");
      if (this.selectedAudienceTypes.includes("new"))
        labels.push("New Customers");
      if (this.selectedAudienceTypes.includes("company"))
        labels.push("Company");
      if (labels.length === 0) return "None selected";
      if (labels.length === 3) return "All Customer Types";
      return labels.join(", ");
    },

    // How many of the currently selected recipients will go out today vs.
    // get automatically scheduled for once the rolling 24h limit frees up.
    messagingSplitPreview() {
      const ml = this.messagingLimit;
      if (!ml || ml.limit === null) {
        return { sendNow: this.eligibleTotal, deferred: 0 };
      }
      const remaining = Math.max(0, ml.remaining);
      const sendNow = Math.min(this.eligibleTotal, remaining);
      return { sendNow, deferred: this.eligibleTotal - sendNow };
    },

    viewScheduledCount() {
      return this.viewRecipients.filter((r) => r.status === "scheduled").length;
    },
  },

  mounted() {
    this.fetchCustomers({ source: "local" });
    this.fetchTemplates();
    this.fetchCampaigns();
    this.fetchMessagingLimit();
  },

  methods: {
    ...mapActions(["fetchCustomers"]),

    statusClass(status) {
      if (["delivered", "read", "completed", "APPROVED"].includes(status))
        return "bg-green-100 text-green-700";
      if (["failed", "REJECTED"].includes(status))
        return "bg-red-100 text-red-700";
      if (status === "scheduled") return "bg-blue-100 text-blue-700";
      return "bg-yellow-100 text-yellow-700";
    },

    formatScheduledAt(value) {
      if (!value) return "";
      try {
        return new Date(value).toLocaleString();
      } catch (e) {
        return value;
      }
    },

    async fetchCampaigns() {
      this.loadingCampaigns = true;
      try {
        const res = await marketingApi.get("/whatsapp/campaigns");
        // index() returns { campaigns, messaging_limit }
        this.campaigns = res.data?.campaigns || [];
        if (res.data?.messaging_limit) {
          this.messagingLimit = res.data.messaging_limit;
        }
      } catch (e) {
        this.toastError(
          e?.response?.data?.message || "Failed to load campaigns.",
        );
      } finally {
        this.loadingCampaigns = false;
      }
    },

    async fetchMessagingLimit() {
      try {
        const res = await marketingApi.get(
          "/whatsapp/campaigns/messaging-limit",
        );
        this.messagingLimit = res.data;
      } catch (e) {
        // non-fatal — banner just won't show if this fails
      }
    },

    async fetchTemplates() {
      try {
        const res = await marketingApi.get("/whatsapp/templates");
        this.templates = res.data || [];
      } catch (e) {
        this.toastError(
          e?.response?.data?.message || "Failed to load templates.",
        );
      }
    },

    openAdd() {
      this.modalMode = "add";
      this.modalOpen = true;
      this.selectedTemplateName = "";
      this.variableInputs = {};
      this.selectedAudienceTypes = ["our", "new", "company"];
      this.usersSearch = "";
      this.selectedUserIds = [];
      this.formError = "";
      this.removeHeaderImage();
      this.fetchMessagingLimit();
    },

    async openView(row) {
      this.modalMode = "view";
      this.modalOpen = true;
      this.loadingDetail = true;
      try {
        const res = await marketingApi.get("/whatsapp/campaigns/" + row.id);
        this.viewCampaign = res.data.campaign;
        this.viewRecipients = res.data.recipients || [];
      } catch (e) {
        this.toastError("Failed to load campaign details.");
        this.viewCampaign = row;
        this.viewRecipients = [];
      } finally {
        this.loadingDetail = false;
      }
    },

    closeModal() {
      this.modalOpen = false;
    },

    onTemplateChange() {
      this.removeHeaderImage();
      if (
        this.selectedTemplate?.header_type === "IMAGE" &&
        this.selectedTemplate?.header_example_url
      ) {
        this.headerImageUrl = this.selectedTemplate.header_example_url;
        this.headerImagePreview = this.selectedTemplate.header_example_url;
      }
    },

    async onHeaderImageChange(e) {
      const file = e.target.files?.[0];
      if (!file) return;

      this.headerImageFile = file;
      if (
        this.headerImagePreview &&
        this.headerImagePreview.startsWith("blob:")
      ) {
        URL.revokeObjectURL(this.headerImagePreview);
      }
      this.headerImagePreview = URL.createObjectURL(file);

      this.uploadingHeaderImage = true;
      try {
        const formData = new FormData();
        formData.append("image", file);
        const res = await marketingApi.post(
          "/whatsapp/upload-campaign-image",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );
        this.headerImageUrl = res.data.url;
      } catch (err) {
        this.toastError("Failed to upload header image.");
        this.removeHeaderImage();
      } finally {
        this.uploadingHeaderImage = false;
      }
    },

    removeHeaderImage() {
      this.headerImageFile = null;
      this.headerImageUrl = "";
      if (
        this.headerImagePreview &&
        this.headerImagePreview.startsWith("blob:")
      ) {
        URL.revokeObjectURL(this.headerImagePreview);
      }
      this.headerImagePreview = "";
      if (this.$refs.headerImageInput) this.$refs.headerImageInput.value = "";
    },

    toggleUser(id) {
      const idx = this.selectedUserIds.indexOf(id);
      if (idx >= 0) this.selectedUserIds.splice(idx, 1);
      else this.selectedUserIds.push(id);
    },

    toggleAllCurrent() {
      if (this.allCurrentSelected) {
        this.selectedUserIds = [];
      } else {
        this.selectedUserIds = this.currentUsersForPicker.map((u) => u.id);
      }
    },

    async submitCampaign() {
      if (this.sending) return;
      this.formError = "";

      if (!this.selectedTemplateName) {
        this.formError = "Select a template.";
        return;
      }
      if (
        this.selectedTemplate?.header_type === "IMAGE" &&
        !this.headerImageUrl
      ) {
        this.formError = "This template requires a header image.";
        return;
      }
      if (this.selectedAudienceTypes.length === 0) {
        this.formError = "Please select at least one target audience group.";
        return;
      }
      const missingVar = this.manualVariables.find(
        (v) => !this.variableInputs[v]?.trim(),
      );
      if (missingVar) {
        this.formError = 'Fill in a value for "' + missingVar + '".';
        return;
      }
      if (!this.eligibleTotal) {
        this.formError =
          "No eligible recipients found for the selected groups.";
        return;
      }

      this.sending = true;
      try {
        const variableValues = (this.selectedTemplate.variables || []).map(
          (v) =>
            v === "customer_name"
              ? "{{customer_name}}"
              : this.variableInputs[v],
        );

        const res = await marketingApi.post("/whatsapp/campaigns", {
          name: this.selectedTemplateName + " - " + new Date().toLocaleString(),
          template_name: this.selectedTemplateName,
          language: this.selectedTemplate.language,
          category: "custom",
          audience_label: this.audienceLabelSummary,
          variable_values: variableValues,
          custom_phones: this.resolvedPhones.filter(Boolean),
          header_image_url: this.headerImageUrl || null,
        });

        const sentNow = res.data?.sent_now_count;
        const deferred = res.data?.deferred_count;
        const successMsg =
          deferred > 0
            ? `Sent to ${sentNow} now — ${deferred} scheduled once the daily limit resets.`
            : "Campaign queued";

        if (res.data?.messaging_limit) {
          this.messagingLimit = res.data.messaging_limit;
        }

        await this.toastSuccess(successMsg);
        this.closeModal();
        await this.fetchCampaigns();
        await this.fetchMessagingLimit();
      } catch (e) {
        this.formError =
          e?.response?.data?.message || "Failed to send campaign.";
      } finally {
        this.sending = false;
      }
    },

    toastSuccess(title) {
      return Swal.fire({
        icon: "success",
        title,
        toast: true,
        position: "top",
        timer: 1400,
        showConfirmButton: false,
      });
    },
    toastError(message) {
      return Swal.fire({
        icon: "error",
        title: "Error",
        text: message,
        toast: true,
        position: "top",
        timer: 2200,
        showConfirmButton: false,
      });
    },
  },
};
</script>

<style scoped>
@reference "tailwindcss";

:deep(td) {
  @apply text-base;
}
</style>
